import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * RATrackerPage — Manager view tracking team's Risk Assessment tasks.
 *
 * Shown to: process_manager, it_function_manager, app_manager, department_head.
 * Allows: review (approve/reject), nudge, escalate.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ShieldCheck, RefreshCw, Bell, AlertTriangle, CheckCircle2,
  XCircle, TrendingUp, Users, ArrowUpCircle,
} from "lucide-react";

const STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-700",
  in_progress: "bg-blue-100 text-blue-700",
  submitted: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700",
  overdue: "bg-red-100 text-red-800",
};

export default function RATrackerPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [filter, setFilter] = useState("all");
  const [reviewTask, setReviewTask] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewComments, setReviewComments] = useState("");
  const [escalateTask, setEscalateTask] = useState(null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateTo, setEscalateTo] = useState("department_head");
  const [escalateReason, setEscalateReason] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/ra-workflow/team-tracker");
      setTasks(res.data.tasks || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      toast.error("Failed to load team tracker");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const nudge = async (taskId) => {
    try {
      await api.post(`/ra-workflow/tasks/${taskId}/nudge`, { message: "Please complete your RA." });
      toast.success("Owner nudged");
      fetchData();
    } catch (err) {
      toast.error("Nudge failed");
    }
  };

  const decide = async (decision) => {
    if (!reviewTask) return;
    try {
      await api.post(`/ra-workflow/tasks/${reviewTask.id}/review`, {
        decision, comments: reviewComments,
      });
      toast.success(`RA ${decision}d`);
      setReviewOpen(false);
      setReviewComments("");
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Review failed"));
    }
  };

  const sendEscalation = async () => {
    if (!escalateTask) return;
    try {
      await api.post(`/ra-workflow/tasks/${escalateTask.id}/escalate`, {
        to_role: escalateTo, reason: escalateReason,
      });
      toast.success(`Escalated to ${escalateTo}`);
      setEscalateOpen(false);
      setEscalateReason("");
      fetchData();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Escalation failed"));
    }
  };

  const filtered = filter === "all"
    ? tasks
    : tasks.filter((t) => (t.effective_status || t.status) === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <RefreshCw className="w-6 h-6 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ra-tracker-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex gap-2 mb-2">
            <Badge className="bg-sky-100 text-sky-700">Manager View</Badge>
            <Badge variant="outline">ISO 22301 § 8.2.3</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Team RA Tracker</h1>
          <p className="text-slate-600 mt-1">
            Track, nudge, review, and escalate Risk Assessments for assets your team owns.
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-tracker">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          ["all", "Total", tasks.length, "bg-slate-50 text-slate-700"],
          ["not_started", "Not Started", summary.not_started || 0, "bg-slate-100 text-slate-700"],
          ["in_progress", "In Progress", summary.in_progress || 0, "bg-blue-50 text-blue-700"],
          ["submitted", "Awaiting Review", summary.submitted || 0, "bg-amber-50 text-amber-700"],
          ["approved", "Approved", summary.approved || 0, "bg-emerald-50 text-emerald-700"],
          ["overdue", "Overdue", summary.overdue || 0, "bg-red-50 text-red-700"],
        ].map(([key, label, val, cls]) => (
          <Card
            key={key}
            onClick={() => setFilter(key)}
            className={`cursor-pointer transition ${cls} ${filter === key ? "ring-2 ring-sky-500" : ""}`}
            data-testid={`filter-${key}`}
          >
            <CardContent className="pt-4">
              <p className="text-xs">{label}</p>
              <p className="text-2xl font-bold">{val}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" /> Team Assessments ({filtered.length})
          </CardTitle>
          <CardDescription>
            {filter === "all" ? "All RA tasks" : `Filtered by: ${filter.replace("_", " ")}`}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No RA tasks in this view.</div>
          ) : (
            <div className="divide-y">
              {filtered.map((t) => (
                <div key={t.id} className="p-4 flex items-center justify-between"
                  data-testid={`tracker-row-${t.id}`}>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{t.asset_name}</p>
                    <p className="text-xs text-slate-500">
                      {t.asset_ref_type} • Due {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}
                      {t.days_open > 0 && ` • ${t.days_open}d open`}
                      {t.nudge_count > 0 && ` • ${t.nudge_count} nudge(s)`}
                      {t.escalation_level > 1 && ` • Escalated L${t.escalation_level}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.residual_risk && (
                      <Badge variant="outline">Residual: {t.residual_risk}</Badge>
                    )}
                    <Badge className={STATUS_COLORS[t.effective_status] || STATUS_COLORS.not_started}>
                      {(t.effective_status || t.status || "not_started").replace("_", " ")}
                    </Badge>
                    {["not_started", "in_progress", "overdue"].includes(t.effective_status) && (
                      <Button size="sm" variant="outline" onClick={() => nudge(t.id)}
                        data-testid={`nudge-${t.id}`}>
                        <Bell className="w-4 h-4 mr-1" /> Nudge
                      </Button>
                    )}
                    {t.effective_status === "submitted" && (
                      <Button size="sm" className="bg-sky-700 hover:bg-sky-800"
                        onClick={() => {
                          setReviewTask(t); setReviewOpen(true);
                        }}
                        data-testid={`review-${t.id}`}>
                        <ShieldCheck className="w-4 h-4 mr-1" /> Review
                      </Button>
                    )}
                    {["overdue", "in_progress"].includes(t.effective_status) && (
                      <Button size="sm" variant="outline"
                        onClick={() => {
                          setEscalateTask(t); setEscalateOpen(true);
                        }}
                        data-testid={`escalate-${t.id}`}>
                        <ArrowUpCircle className="w-4 h-4 mr-1" /> Escalate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-2xl" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle>Review Risk Assessment</DialogTitle>
            <DialogDescription>{reviewTask?.asset_name}</DialogDescription>
          </DialogHeader>
          {reviewTask && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><strong>Threat Source:</strong> {reviewTask.form?.threat_source || "—"}</div>
                <div><strong>Threat Event:</strong> {reviewTask.form?.threat_event || "—"}</div>
                <div><strong>Vulnerability:</strong> {reviewTask.form?.vulnerability || "—"}</div>
                <div><strong>Treatment:</strong> {reviewTask.form?.treatment || "—"}</div>
                <div><strong>Inherent Risk:</strong> {reviewTask.inherent_risk || "—"}</div>
                <div><strong>Residual Risk:</strong> {reviewTask.residual_risk || "—"}</div>
              </div>
              {reviewTask.form?.treatment_plan && (
                <div>
                  <strong>Treatment Plan:</strong>
                  <p className="text-slate-600 mt-1">{reviewTask.form.treatment_plan}</p>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold">Review Comments</label>
                <Textarea rows={3} value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  placeholder="Optional comments (required for reject)"
                  data-testid="review-comments" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewOpen(false)}>Close</Button>
            <Button variant="outline" className="text-red-700 border-red-300"
              onClick={() => decide("reject")} data-testid="reject-btn">
              <XCircle className="w-4 h-4 mr-2" /> Reject
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => decide("approve")} data-testid="approve-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
        <DialogContent className="max-w-md" data-testid="escalate-dialog">
          <DialogHeader>
            <DialogTitle>Escalate RA Task</DialogTitle>
            <DialogDescription>{escalateTask?.asset_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Escalate to</label>
              <Select value={escalateTo} onValueChange={setEscalateTo}>
                <SelectTrigger data-testid="escalate-to"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="department_head">Department Head</SelectItem>
                  <SelectItem value="bcm_manager">BCM Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold">Reason</label>
              <Textarea rows={3} value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                placeholder="e.g., Owner unresponsive after 2 nudges, overdue 10 days"
                data-testid="escalate-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEscalateOpen(false)}>Cancel</Button>
            <Button onClick={sendEscalation} className="bg-amber-600 hover:bg-amber-700"
              data-testid="send-escalation">
              <ArrowUpCircle className="w-4 h-4 mr-2" /> Escalate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
